import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { OrderSchemaType, ItemPath } from '../../../../order.schema';
import ApiService from '../../../../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../../../../contexts/FeedbackContext';
import { FormGroup, Input, Label, Select } from '../../../../style';
import { BlockInput, CalcBadge, CalcHint, CalculatedValue, DimensionsGrid, FullRowFormGroup, WeightField, WeightInput } from './FinalProductDimensionsSection.style';
import DimensionToleranceInput from './DimensionToleranceInput';
import { useWeightAutoFill } from '../../../../Hooks/useWeightAutoFill';

interface FinalProductDimensionsSectionProps {
  itemPath: ItemPath;
}

type DimColumn = 'diameter' | 'thickness' | 'width';

interface FormatDimensionRule {
  dim1: DimColumn;
  dim2?: DimColumn;
}

export const FORMAT_DIMENSIONS: Record<string, FormatDimensionRule> = {
  redondo: { dim1: 'diameter' },
  sextavado: { dim1: 'diameter' },
  quadrado: { dim1: 'thickness', dim2: 'width' },
  chapa: { dim1: 'thickness', dim2: 'width' },
  tubo: { dim1: 'thickness', dim2: 'diameter' },
  anel: { dim1: 'thickness', dim2: 'diameter' },
  tubular: { dim1: 'thickness', dim2: 'diameter' },
};

// Formatos em que dim2 é o Ø interno — lá a tolerância que engrossa a parede é a negativa.
const HOLLOW_FORMATS = new Set(['tubo', 'anel', 'tubular']);

export const hasDimensionsRule = (formatName: string | undefined): boolean =>
  !!FORMAT_DIMENSIONS[String(formatName || '').toLowerCase().trim()];

// Rótulo pelo nome real da dimensão, na mesma chave de FORMAT_DIMENSIONS: no tubo, a coluna
// `thickness` guarda o Ø externo e `diameter` o Ø interno.
const FORMAT_LABELS: Record<string, { dim1: string; dim2?: string }> = {
  redondo: { dim1: 'Diâmetro (mm)' },
  sextavado: { dim1: 'Diâmetro (mm)' },
  quadrado: { dim1: 'Espessura (mm)', dim2: 'Largura (mm)' },
  chapa: { dim1: 'Espessura (mm)', dim2: 'Largura (mm)' },
  tubo: { dim1: 'Ø externo (mm)', dim2: 'Ø interno (mm)' },
  anel: { dim1: 'Ø externo (mm)', dim2: 'Ø interno (mm)' },
  tubular: { dim1: 'Ø externo (mm)', dim2: 'Ø interno (mm)' },
};

interface ReferenceMaterial {
  materialId: number;
  materialName: string;
  density: number;
}

const round4 = (value: number) => Math.round(value * 10000) / 10000;

const materialDensityCache = new Map<number, ReferenceMaterial | null>();

const fetchMaterialDensity = async (materialId: number): Promise<ReferenceMaterial | null> => {
  const cached = materialDensityCache.get(materialId);
  if (cached !== undefined) return cached;

  const res = await ApiService.steelOrders.getMaterials({ id: materialId });
  const raw = Array.isArray(res.data) ? res.data[0] : res.data;
  const density = Number(raw?.density);
  const resolved: ReferenceMaterial | null = raw && !isNaN(density) && density > 0
    ? { materialId, materialName: raw.name || '', density }
    : null;
  materialDensityCache.set(materialId, resolved);
  return resolved;
};

const FinalProductDimensionsSection: React.FC<FinalProductDimensionsSectionProps> = ({ itemPath }) => {
  const { control, setValue, getValues } = useFormContext<OrderSchemaType>();
  const { setFeedback } = useContext(FeedbackContext);
  const isTopLevelItem = !itemPath.includes('.subItems.');

  const notifyError = useCallback((message: string) => {
    setFeedback({ message, isOpen: true, statusOk: false });
  }, [setFeedback]);

  const [formats, setFormats] = useState<{ id: number; name: string }[]>([]);
  const [materials, setMaterials] = useState<ReferenceMaterial[]>([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | undefined>(undefined);
  const [calculatedMass, setCalculatedMass] = useState<number | null>(null);

  const formatId = useWatch({ control, name: `${itemPath}.formatId` as any }) as number | undefined;
  const scriptId = useWatch({ control, name: `${itemPath}.scriptId` as any }) as number | undefined;
  const lengthValue = useWatch({ control, name: `${itemPath}.length` as any }) as number | string | undefined;
  const weightValue = useWatch({ control, name: `${itemPath}.weight` as any }) as number | string | undefined;
  const quantityPiecesValue = useWatch({ control, name: `${itemPath}.quantityPieces` as any }) as number | string | undefined;
  const diameterValue = useWatch({ control, name: `${itemPath}.diameter` as any }) as number | string | undefined;
  const thicknessValue = useWatch({ control, name: `${itemPath}.thickness` as any }) as number | string | undefined;
  const widthValue = useWatch({ control, name: `${itemPath}.width` as any }) as number | string | undefined;
  // A massa também muda quando só a tolerância muda — sem observar, o valor congela.
  const toleranceValues = useWatch({
    control,
    name: [
      `${itemPath}.diameterTolerancePlus`, `${itemPath}.diameterToleranceMinus`,
      `${itemPath}.thicknessTolerancePlus`, `${itemPath}.thicknessToleranceMinus`,
      `${itemPath}.widthTolerancePlus`, `${itemPath}.widthToleranceMinus`,
      `${itemPath}.lengthTolerancePlus`, `${itemPath}.lengthToleranceMinus`,
    ] as any,
  });

  const subItemsWatch = useWatch({ control, name: `${itemPath}.subItems` as any }) as any[] | undefined;
  const subItemMaterialIdsKey = (subItemsWatch || [])
    .map(subItem => Number(subItem?.materialId))
    .filter(id => !!id && !isNaN(id))
    .join(',');
  const subItemMaterialIds = useMemo(
    () => (subItemMaterialIdsKey ? subItemMaterialIdsKey.split(',').map(Number) : []),
    [subItemMaterialIdsKey],
  );

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const calcFailedRef = useRef(false);

  // Massa por peça vai para o form (transitória) para o peso e a composição lerem.
  const publishMass = useCallback((mass: number | null) => {
    setCalculatedMass(mass);
    const current = getValues(`${itemPath}.massPerPiece` as any) as any;
    const next = mass ?? undefined;
    if (current !== next) setValue(`${itemPath}.massPerPiece` as any, next, { shouldDirty: false, shouldValidate: false });
  }, [getValues, setValue, itemPath]);

  useEffect(() => {
    if (!isTopLevelItem) return;
    let active = true;
    ApiService.stockLookup.getFormats()
      .then(res => { if (active) setFormats(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (active) notifyError('Não foi possível carregar os formatos. O cálculo de massa fica indisponível.'); });
    return () => { active = false; };
  }, [isTopLevelItem, notifyError]);

  useEffect(() => {
    if (!isTopLevelItem) return;
    let active = true;

    const applyMaterials = (mats: ReferenceMaterial[]) => {
      if (!active) return;
      setMaterials(mats);
      setSelectedMaterialId(mats[0]?.materialId);
    };

    if (scriptId) {
      const quantity = Number(getValues(`${itemPath}.quantity` as any)) || 1;
      ApiService.steelOrders.getScriptBreakdown(Number(scriptId), { quantity })
        .then(res => {
          const mats: ReferenceMaterial[] = (Array.isArray(res.data?.materials) ? res.data.materials : [])
            .filter((m: any) => m?.density !== undefined && m?.density !== null)
            .map((m: any) => ({
              materialId: Number(m.materialId),
              materialName: m.materialName || '',
              density: Number(m.density),
            }));
          applyMaterials(mats);
        })
        .catch(() => { if (active) notifyError('Não foi possível carregar a densidade do roteiro. A massa não será calculada.'); });
      return () => { active = false; };
    }

    const materialIds = (subItemMaterialIds || []).filter(Boolean);
    if (materialIds.length === 0) {
      applyMaterials([]);
      return () => { active = false; };
    }

    Promise.all(materialIds.map(id => fetchMaterialDensity(id)))
      .then(results => applyMaterials(results.filter(Boolean) as ReferenceMaterial[]))
      .catch(() => { if (active) notifyError('Não foi possível carregar a densidade da matéria-prima. A massa não será calculada.'); });

    return () => { active = false; };
  }, [scriptId, isTopLevelItem, itemPath, getValues, subItemMaterialIds, notifyError]);

  const resolveFormatType = useCallback((): string => {
    const fid = Number(formatId);
    if (!fid) return '';
    const match = formats.find(f => Number(f.id) === fid);
    return match ? String(match.name || '').toLowerCase().trim() : '';
  }, [formatId, formats]);

  const resolveDensity = useCallback((): number | null => {
    if (materials.length === 0) return null;
    if (materials.length === 1) return materials[0].density;
    const selected = materials.find(m => m.materialId === selectedMaterialId) || materials[0];
    return selected.density;
  }, [materials, selectedMaterialId]);

  // Massa por peça a partir de formato, dimensões, comprimento e densidade. O caminho inverso
  // (peso → comprimento) saiu: o peso agora é derivado da massa (useWeightAutoFill).
  const runCalc = useCallback(async () => {
    const formatType = resolveFormatType();
    const rule = FORMAT_DIMENSIONS[formatType];
    if (!rule) return;
    const density = resolveDensity();
    if (!density) return;

    const dim1Raw = Number(getValues(`${itemPath}.${rule.dim1}` as any)) || 0;
    const dim2Raw = rule.dim2 ? (Number(getValues(`${itemPath}.${rule.dim2}` as any)) || 0) : 0;
    const lengthRaw = Number(getValues(`${itemPath}.length` as any));
    if (!lengthRaw || !isFinite(lengthRaw)) return;

    // A massa vai para o lado que a tolerância maximiza. Nos vazados a regra é assimétrica:
    // o Ø externo cresce, mas o Ø interno ENCOLHE — furo menor é parede mais grossa.
    const withTolerance = (base: number, column: string, direction: 'plus' | 'minus'): number => {
      if (!base) return base;
      const suffix = direction === 'plus' ? 'TolerancePlus' : 'ToleranceMinus';
      const tol = Number(getValues(`${itemPath}.${column}${suffix}` as any)) || 0;
      if (!tol) return base;
      return direction === 'plus' ? base + tol : Math.max(base - tol, 0);
    };

    const isHollow = HOLLOW_FORMATS.has(formatType);
    const dim1 = withTolerance(dim1Raw, rule.dim1, 'plus');
    const dim2 = rule.dim2 ? withTolerance(dim2Raw, rule.dim2, isHollow ? 'minus' : 'plus') : 0;
    const length = withTolerance(lengthRaw, 'length', 'plus');

    try {
      const res = await ApiService.pcp.calculateByFormat({ formatType, dim1, dim2, density, quantity: length, unit: 'MM' });
      const mass = res.data?.totalWeight;
      if (typeof mass === 'number' && isFinite(mass)) {
        calcFailedRef.current = false;
        publishMass(round4(mass));
      }
    } catch {
      publishMass(null);
      if (calcFailedRef.current) return;
      calcFailedRef.current = true;
      notifyError('Não foi possível calcular a massa. Confira se o formato cadastrado é reconhecido.');
    }
  }, [resolveFormatType, resolveDensity, getValues, itemPath, notifyError, publishMass]);

  const scheduleCalc = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { runCalc(); }, 500);
  }, [runCalc]);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  useEffect(() => {
    if (!isTopLevelItem) return;
    const hasRule = !!FORMAT_DIMENSIONS[resolveFormatType()];
    const length = Number(getValues(`${itemPath}.length` as any));
    if (!hasRule || !resolveDensity() || !length || !isFinite(length)) {
      publishMass(null);
      return;
    }
    scheduleCalc();
  }, [
    isTopLevelItem,
    resolveFormatType,
    resolveDensity,
    getValues,
    itemPath,
    scheduleCalc,
    publishMass,
    diameterValue,
    thicknessValue,
    widthValue,
    lengthValue,
    toleranceValues,
  ]);

  const formatType = resolveFormatType();
  const rule = FORMAT_DIMENSIONS[formatType];
  const labels = FORMAT_LABELS[formatType] ?? { dim1: 'Dimensão 1 (mm)', dim2: 'Dimensão 2 (mm)' };
  const activeDensity = resolveDensity();

  const displayMass = calculatedMass;
  const { autoWeight, isWeightAuto } = useWeightAutoFill({ itemPath, enabled: isTopLevelItem && !!formatId && !!rule });
  const piecesNumber = Number(quantityPiecesValue);
  const hasPieces = Number.isFinite(piecesNumber) && piecesNumber > 0;
  const hasWeight = weightValue !== undefined && weightValue !== null && weightValue !== '';

  let weightHint: string;
  if (isWeightAuto) weightHint = 'massa × peças · digite para fixar, limpe para recalcular';
  else if (autoWeight !== null && hasWeight) weightHint = 'valor digitado · limpe para voltar ao cálculo';
  else if (displayMass !== null && !hasPieces) weightHint = 'informe a quantidade de peças para calcular';
  else weightHint = 'Usado na OP e na baixa de estoque';

  if (!isTopLevelItem || !formatId || !rule) return null;

  return (
    <DimensionsGrid columns={3}>
      <DimensionToleranceInput itemPath={itemPath} column={rule.dim1} label={labels.dim1} />

      {rule.dim2 ? (
        <DimensionToleranceInput itemPath={itemPath} column={rule.dim2} label={labels.dim2 ?? 'Dimensão 2 (mm)'} />
      ) : (
        <FormGroup />
      )}

      <DimensionToleranceInput itemPath={itemPath} column="length" label="Comprimento (mm)" />

      <FormGroup>
        <Label>Massa por peça (kg)</Label>
        <CalculatedValue>
          {displayMass !== null
            ? displayMass.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
            : '—'}
        </CalculatedValue>
        {displayMass !== null && activeDensity && (
          <CalcHint>por peça · {formatType} · densidade {activeDensity}</CalcHint>
        )}
        {displayMass === null && !activeDensity && (
          <CalcHint>Sem densidade — informe o roteiro ou a matéria-prima do item.</CalcHint>
        )}
        {displayMass === null && !!activeDensity && (
          <CalcHint>Preencha as dimensões e o comprimento (mm).</CalcHint>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor={`${itemPath}.weight`}>Peso total (kg)</Label>
        <WeightField>
          <WeightInput
            id={`${itemPath}.weight`}
            autoComplete="off"
            type="number"
            step="any"
            value={(weightValue as any) ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              setValue(`${itemPath}.weight` as any, raw === '' ? undefined : Number(raw), { shouldDirty: true, shouldValidate: false });
            }}
            placeholder="Digite"
          />
          {isWeightAuto && <CalcBadge>Calculado</CalcBadge>}
        </WeightField>
        <CalcHint>{weightHint}</CalcHint>
      </FormGroup>

      <FormGroup>
        <Label htmlFor={`${itemPath}.quantityPieces`}>Quantidade de peças</Label>
        <BlockInput
          id={`${itemPath}.quantityPieces`}
          autoComplete="off"
          type="number"
          step="1"
          min="0"
          value={(quantityPiecesValue as any) ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            setValue(`${itemPath}.quantityPieces` as any, raw === '' ? undefined : Number(raw), { shouldDirty: true, shouldValidate: false });
          }}
          placeholder="Digite"
        />
        <CalcHint>massa/pç × peças</CalcHint>
      </FormGroup>

      {materials.length > 1 && (
        <FullRowFormGroup>
          <Label htmlFor={`${itemPath}.referenceMaterial`}>Material de referência (densidade)</Label>
          <Select
            id={`${itemPath}.referenceMaterial`}
            value={selectedMaterialId ?? ''}
            onChange={(e) => setSelectedMaterialId(e.target.value ? Number(e.target.value) : undefined)}
          >
            {materials.map(m => (
              <option key={m.materialId} value={m.materialId}>{m.materialName} ({m.density})</option>
            ))}
          </Select>
          <CalcHint>Usado no cálculo da massa por peça.</CalcHint>
        </FullRowFormGroup>
      )}
    </DimensionsGrid>
  );
};

export default FinalProductDimensionsSection;
